import { useEffect, useState } from 'react';
import type { CSSProperties } from 'react';
import { countryNameFor, flagAssetPath, normalizeCountryCode } from '../../config/countryCatalog';

/**
 * Country Flag System V1 — the one shared way to render a country flag.
 *
 * Accepts a raw, possibly-null/unknown code and validates it against the
 * 250-code catalog BEFORE any asset path is built, so server payloads or
 * stale values can never turn into an arbitrary URL. Unset, unsupported,
 * and failed-to-load codes all render the same neutral globe placeholder —
 * never a broken-image icon.
 *
 * Standard sizes are 24×18 and 32×24 (4:3 preserved via width → height*3/4
 * when height is omitted). Use `loading="eager"` for game/lobby surfaces
 * and the default lazy loading for long lists (friends, leaderboard).
 */
export interface CountryFlagProps {
  /** Raw country code from any source — validated internally. */
  countryCode?: string | null;
  /** Rendered width in px (default 24). Height defaults to width × 3/4. */
  width?: number;
  height?: number;
  /** Decorative flags render with empty alt text and are hidden from AT. */
  decorative?: boolean;
  loading?: 'eager' | 'lazy';
  style?: CSSProperties;
}

/** Neutral globe placeholder — shared fallback for unset/invalid/failed. */
function GlobePlaceholder({ width, height, decorative, style }: {
  width: number; height: number; decorative: boolean; style?: CSSProperties;
}) {
  return (
    <span
      role={decorative ? undefined : 'img'}
      aria-label={decorative ? undefined : 'Country not set'}
      aria-hidden={decorative || undefined}
      title={decorative ? undefined : 'Country not set'}
      data-flag-placeholder="true"
      style={{
        display:        'inline-flex',
        alignItems:     'center',
        justifyContent: 'center',
        width,
        height,
        borderRadius:   3,
        border:         '1px solid rgba(255,255,255,0.25)',
        background:     'rgba(255,255,255,0.06)',
        flexShrink:     0,
        ...style,
      }}
    >
      <svg
        width={Math.round(height * 0.72)}
        height={Math.round(height * 0.72)}
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
      >
        <circle cx="12" cy="12" r="10" stroke="rgba(255,255,255,0.45)" strokeWidth="1.5" />
        <ellipse cx="12" cy="12" rx="4.5" ry="10" stroke="rgba(255,255,255,0.45)" strokeWidth="1.5" />
        <path d="M2.5 8.5h19M2.5 15.5h19" stroke="rgba(255,255,255,0.45)" strokeWidth="1.5" />
      </svg>
    </span>
  );
}

export function CountryFlag({
  countryCode,
  width = 24,
  height,
  decorative = false,
  loading = 'lazy',
  style,
}: CountryFlagProps) {
  const code = normalizeCountryCode(countryCode);
  const [failedSrc, setFailedSrc] = useState<string | null>(null);

  // A new (valid) code gets a fresh chance even after a previous load error.
  useEffect(() => { setFailedSrc(null); }, [code]);

  const h = height ?? Math.round(width * 0.75);
  const src = code ? flagAssetPath(code) : null;

  if (!code || !src || failedSrc === src) {
    return <GlobePlaceholder width={width} height={h} decorative={decorative} style={style} />;
  }

  const name = countryNameFor(code) ?? code.toUpperCase();
  return (
    <img
      src={src}
      alt={decorative ? '' : `${name} flag`}
      aria-hidden={decorative || undefined}
      title={decorative ? undefined : name}
      width={width}
      height={h}
      loading={loading}
      draggable={false}
      onError={() => setFailedSrc(src)}
      style={{
        width,
        height:       h,
        objectFit:    'contain',
        borderRadius: 3,
        display:      'inline-block',
        flexShrink:   0,
        ...style,
      }}
    />
  );
}
